import { useMemo } from 'react';
import SEO from '../components/SEO';
import { mentors } from '../data/mentors';
import type { Mentor } from '../data/mentors';
import { useMentorModal } from '../hooks/useMentorModal';
import { createWebsiteLd, createOrganizationLd, createPersonLd } from '../utils/schema';
import { useLocation } from 'react-router-dom';
import { useInView } from '../hooks/useInView';

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = shuffled[i]!;
    shuffled[i] = shuffled[j]!;
    shuffled[j] = temp;
  }
  return shuffled;
};

// Move shuffling outside component to prevent re-shuffling
const allMentors = shuffleArray(mentors);
const firstHalfMentors = allMentors.slice(0, Math.ceil(allMentors.length / 2));
const secondHalfMentors = allMentors.slice(Math.ceil(allMentors.length / 2));

const LandingPage: React.FC = () => {
  const { openMentorModal } = useMentorModal();
  const location = useLocation();

  // Get mentorId from query parameters - handle both cases
  const searchParams = new URLSearchParams(location.search);
  const mentorId = searchParams.get('mentorId') || searchParams.get('mentorid');

  // Find the mentor if mentorId is present
  const selectedMentor = useMemo(() => {
    if (mentorId) {
      return mentors.find((m) => m.id === mentorId);
    }
    return null;
  }, [mentorId]);

  // Create schema data
  const schema = useMemo(() => {
    const websiteSchema = createWebsiteLd();
    const organizationSchema = createOrganizationLd();

    return {
      ...websiteSchema,
      '@type': 'WebSite',
      provider: organizationSchema,
      offers: {
        '@type': 'Offer',
        name: 'Game Industry Mentorship',
        description: 'Professional mentorship services for game industry professionals and studios',
        category: 'Professional Services',
      },
      audience: {
        '@type': 'Audience',
        audienceType: 'Game Industry Professionals',
      },
    } as const;
  }, []);

  // Create schema for the selected mentor if one is selected
  const mentorSchema = useMemo(() => {
    if (selectedMentor) {
      return createPersonLd(
        selectedMentor.name,
        `https://gamementors.be/mentors/${selectedMentor.id}`,
        selectedMentor.image,
        selectedMentor.description,
        selectedMentor.expertise?.[0],
        [selectedMentor.linkedIn, selectedMentor.twitter, selectedMentor.website].filter(
          (url): url is string => !!url
        )
      );
    }
    return undefined;
  }, [selectedMentor]);

  // Note: URL handling for mentor modals is now managed by MentorModalContext

  const suggestionsSection = useInView();

  // Helper function to get base color
  const getBaseColor = (expertise: string[] | undefined) => {
    if (!expertise || expertise.length === 0) return 'gray';
    const firstExpertise = expertise[0];
    const mapping: Record<string, string> = {
      'Business & Strategy': 'blue',
      'Game Design & Creative': 'purple',
      'Technology & Development': 'cyan',
      'Marketing & Community': 'green',
      'Production & Management': 'orange',
      'Legal & IP': 'red',
    };
    return mapping[firstExpertise as keyof typeof mapping] || 'gray';
  };

  // Helper function to get category color
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Business':
        return 'text-blue-600';
      case 'Design':
        return 'text-purple-600';
      case 'Marketing':
        return 'text-green-600';
      case 'Leadership':
        return 'text-orange-600';
      case 'Legal':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // Define mentor assignments for each category
  const categoryMentors = {
    'startup-strategy': [
      'eric-diepeveen',
      'hind-toufga',
      'mehdi-benkirane',
      'michiel-houwen',
      'christian-fonnesbech',
    ],
    'game-monetisation': ['oscar-clark', 'sebastien-borget'],
    'promoting-game': ['bram-van-lith', 'robby-bisschop', 'vernon-vrolijk', 'kevin-haelterman'],
    'pitching-publishers': [
      'paul-hanraets',
      'maarten-de-koning',
      'geoff-van-den-ouden',
      'bram-van-lith',
    ],
    leadership: ['martijn-van-zwieten', 'kevin-haelterman', 'geoff-van-den-ouden'],
    'pitching-investors': [
      'maarten-de-koning',
      'paul-hanraets',
      'rami-ismail',
      'eric-diepeveen',
      'nicolas-vereecke',
    ],
    'grow-audience': ['federica-nocerino', 'richard-kos', 'christian-fonnesbech', 'chris-bain'],
    legal: ['rene-otto', 'olivier-oosterbaan', 'maarten-de-koning', 'caroline-balemans'],
  };

  // Helper function to get mentors by category ID
  const getMentorsForCategory = (categoryId: string): Mentor[] => {
    const mentorIds = categoryMentors[categoryId as keyof typeof categoryMentors] || [];
    return mentorIds
      .map((id) => mentors.find((m) => m.id === id))
      .filter((m): m is Mentor => m !== undefined);
  };

  // Define the categories with their associated expertise
  const mentorshipCategories = [
    {
      id: 'startup-strategy',
      title: 'Startup Strategy',
      category: 'Business',
      expertise: 'Business & Strategy',
      description:
        'Starting a game studio comes with its own set of challenges. These mentors can guide you in developing a strong startup strategy, helping you set clear goals and build the foundation for long-term success in the game industry.',
    },
    {
      id: 'game-monetisation',
      title: 'Game Monetisation Design',
      category: 'Design',
      expertise: 'Game Design & Creative',
      description:
        'Balancing gameplay with effective monetisation strategies is crucial for modern games. These mentors can help design gameplay loops that enhance player engagement while integrating monetisation models that feel natural and fair.',
    },
    {
      id: 'promoting-game',
      title: 'Promoting Your Game',
      category: 'Marketing',
      expertise: 'Marketing & Community',
      description:
        'Launching your game can be one of the most exciting yet challenging milestones in your journey. These mentors know how to help you create effective marketing plans, reach your ideal audience, build community engagement, and turn your game into a success.',
    },
    {
      id: 'pitching-publishers',
      title: 'Pitching to Publishers',
      category: 'Business',
      expertise: 'Business & Strategy',
      description:
        'Pitching to publishers is critical for securing both funding and distribution. These mentors can help you perfect your pitch and guide you through presenting your project in the best possible way to stand out from the competition.',
    },
    {
      id: 'leadership',
      title: 'Leadership and Management Styles',
      category: 'Leadership',
      expertise: 'Production & Management',
      description:
        'Leading a game studio requires more than just a great product. These mentors can help you develop leadership skills to foster a productive and creative work environment.',
    },
    {
      id: 'pitching-investors',
      title: 'Pitching to Investors',
      category: 'Business',
      expertise: 'Business & Strategy',
      description:
        'Delivering a successful pitch is critical for securing investment. These mentors will help you refine your pitch, communicate your vision effectively, and increase your chances of getting the funding you need for growth.',
    },
    {
      id: 'grow-audience',
      title: 'Grow Your Audience',
      category: 'Marketing',
      expertise: 'Marketing & Community',
      description:
        'Acquiring new users and scaling your game requires effective performance marketing strategies. These mentors can help you craft user acquisition plans, optimize ROAS, and guide you through protecting and leveraging your IP.',
    },
    {
      id: 'legal',
      title: 'Negotiations and Contracting',
      category: 'Legal',
      expertise: 'Legal & IP',
      description:
        'Navigating negotiations and drafting contracts is essential for success. These mentors can guide you through protecting your IP, securing favorable terms, and avoiding common legal pitfalls.',
    },
  ];

  // Split categories into two columns
  const midpoint = Math.ceil(mentorshipCategories.length / 2);
  const firstColumnCategories = mentorshipCategories.slice(0, midpoint);
  const secondColumnCategories = mentorshipCategories.slice(midpoint);

  return (
    <>
      <SEO
        title={
          selectedMentor
            ? `${selectedMentor.name} - Game Industry Mentor | GameMentors`
            : 'Unlock the True Potential of Your Game Studio | GameMentors'
        }
        description={
          selectedMentor
            ? selectedMentor.description
            : 'Connect with experienced game industry professionals who can help you navigate challenges, grow your studio, and achieve your goals.'
        }
        ogTitle={
          selectedMentor
            ? `Connect with ${selectedMentor.name} - Game Industry Mentor`
            : 'Unlock the True Potential of Your Game Studio | GameMentors'
        }
        ogDescription={
          selectedMentor
            ? selectedMentor.description
            : 'Connect with experienced game industry professionals who can help you navigate challenges, grow your studio, and achieve your goals.'
        }
        ogImage={selectedMentor?.image || '/assets/logo.webp'}
        ogImageWidth={800}
        ogImageHeight={800}
        ogImageAlt={
          selectedMentor ? `${selectedMentor.name} - Game Industry Mentor` : 'GameMentors Logo'
        }
        twitterTitle={
          selectedMentor
            ? `${selectedMentor.name} - Game Industry Mentor`
            : 'Unlock the True Potential of Your Game Studio | GameMentors'
        }
        twitterDescription={
          selectedMentor
            ? selectedMentor.description
            : 'Connect with experienced game industry professionals who can help you navigate challenges, grow your studio, and achieve your goals.'
        }
        twitterImage={selectedMentor?.image || '/assets/logo.webp'}
        twitterImageAlt={
          selectedMentor ? `${selectedMentor.name} - Game Industry Mentor` : 'GameMentors Logo'
        }
        linkedinTitle={
          selectedMentor
            ? `${selectedMentor.name} - Game Industry Mentor on GameMentors`
            : 'Unlock the True Potential of Your Game Studio | GameMentors'
        }
        linkedinDescription={
          selectedMentor
            ? selectedMentor.description
            : 'Connect with experienced game industry professionals who can help you navigate challenges, grow your studio, and achieve your goals.'
        }
        type={selectedMentor ? 'profile' : 'website'}
        schema={selectedMentor ? mentorSchema : schema}
      />
      {/* ========== HERO SECTION ========== */}
      <div className="relative overflow-hidden md:py-24">
        <div className="mx-auto max-w-[85rem] px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Logo */}
            <div className="mb-6 hidden sm:m-0 sm:mb-8 sm:block md:mb--12">
              <div className="flex items-center justify-center gap-4">
                <img src="/assets/icon.webp" alt="GameMentors" className="h-10 sm:h-12" />
                <img
                  src="/assets/forsvclogo.webp"
                  alt="ForsVC"
                  className="h-8 opacity-80 sm:h-10"
                />
              </div>
            </div>

            {/* Main Title - Apple Style */}
            <h1 className="mb-4 mt-20 text-[1.5rem] font-medium leading-tight tracking-tight text-gray-900 sm:mb-8 sm:mt-0 sm:text-[2.5rem] md:text-[3.5rem] lg:text-[4.5rem]">
              <span className="inline-block">Unlock the True Potential of</span>
              <span className="inline-block">Your Game Studio</span>
            </h1>

            {/* Subtitle - Apple Style */}
            <p className="mx-auto mb-8 max-w-[60ch] text-base font-light leading-relaxed text-gray-500 sm:text-xl">
              <span className="sm:hidden">
                We connect you with experienced game industry professionals who can help you
                navigate challenges, grow your studio, and achieve your goals. Get personalized
                guidance from mentors who've been there before.
              </span>
              <span className="hidden sm:inline">
                We connect you with experienced game industry professionals who can help you
                navigate challenges, grow your studio, and achieve your goals. Get personalized
                guidance from mentors who've been there before.
              </span>
            </p>

            {/* CTA Button - Apple Style */}
            <div className="mb-12 px-4 sm:mb-16 sm:px-0">
              <a
                href="/mentors"
                className="inline-flex w-full items-center justify-center rounded-full bg-red-600 px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red-700 sm:w-auto sm:px-8"
              >
                Find your perfect mentor
              </a>
            </div>

            {/* Scrolling Mentor Gallery */}
            <div className="relative inset-x-1/2 mx-[-50vw] w-screen py-4 sm:py-12">
              <div className="relative overflow-hidden">
                {/* First Row - Scrolling Left */}
                <div className="flex">
                  {/* Original row */}
                  <div className="flex animate-scroll-left gap-2 pl-2 sm:gap-2 sm:pl-2 md:gap-6 md:pl-4">
                    {firstHalfMentors.map((mentor) => (
                      <div
                        key={mentor.id}
                        onClick={() => openMentorModal(mentor)}
                        className="group relative aspect-square h-20 shrink-0 cursor-pointer overflow-hidden rounded-lg shadow-lg sm:h-32 sm:rounded-xl md:h-36 md:rounded-2xl lg:h-32 xl:h-44"
                      >
                        <img
                          src={mentor.image}
                          alt={mentor.name}
                          className="size-full object-cover transition-transform duration-300 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div
                          className="absolute inset-0 z-10 rounded-lg sm:rounded-xl md:rounded-2xl"
                          style={{
                            background:
                              'linear-gradient(to top, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 80%, rgba(0, 0, 0, 0) 100%)',
                          }}
                        />
                        <div className="absolute inset-x-2 bottom-1 z-10 sm:bottom-2 md:inset-x-4 md:bottom-4">
                          <h3 className="px-1 text-[10px] font-semibold text-white sm:text-xs md:text-sm">
                            {mentor.name.split(' ')[0]}
                          </h3>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Duplicate row */}
                  <div
                    className="flex animate-scroll-left gap-2 pl-2 sm:gap-2 sm:pl-2 md:gap-6 md:pl-4"
                    aria-hidden="true"
                  >
                    {firstHalfMentors.map((mentor) => (
                      <div
                        key={`${mentor.id}-duplicate`}
                        onClick={() => openMentorModal(mentor)}
                        className="group relative aspect-square h-20 shrink-0 cursor-pointer overflow-hidden rounded-lg shadow-lg sm:h-32 sm:rounded-xl md:h-36 md:rounded-2xl lg:h-32 xl:h-44"
                      >
                        <img
                          src={mentor.image}
                          alt={mentor.name}
                          className="size-full object-cover transition-transform duration-300 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div
                          className="absolute inset-0 z-10 rounded-lg sm:rounded-xl md:rounded-2xl"
                          style={{
                            background:
                              'linear-gradient(to top, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 80%, rgba(0, 0, 0, 0) 100%)',
                          }}
                        />
                        <div className="absolute inset-x-2 bottom-1 z-10 sm:bottom-2 md:inset-x-4 md:bottom-4">
                          <h3 className="px-1 text-[10px] font-semibold text-white sm:text-xs md:text-sm">
                            {mentor.name.split(' ')[0]}
                          </h3>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Second Row - Scrolling Right */}
                <div className="mt-2 flex sm:mt-2 md:mt-6">
                  {/* Original row */}
                  <div className="flex animate-scroll-right gap-2 pl-2 sm:gap-2 sm:pl-2 md:gap-6 md:pl-4">
                    {secondHalfMentors.map((mentor) => (
                      <div
                        key={mentor.id}
                        onClick={() => openMentorModal(mentor)}
                        className="group relative aspect-square h-20 shrink-0 cursor-pointer overflow-hidden rounded-lg shadow-lg sm:h-32 sm:rounded-xl md:h-36 md:rounded-2xl lg:h-32 xl:h-44"
                      >
                        <img
                          src={mentor.image}
                          alt={mentor.name}
                          className="size-full object-cover transition-transform duration-300 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div
                          className="absolute inset-0 z-10 rounded-lg sm:rounded-xl md:rounded-2xl"
                          style={{
                            background:
                              'linear-gradient(to top, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 80%, rgba(0, 0, 0, 0) 100%)',
                          }}
                        />
                        <div className="absolute inset-x-2 bottom-1 z-10 sm:bottom-2 md:inset-x-4 md:bottom-4">
                          <h3 className="px-1 text-[10px] font-semibold text-white sm:text-xs md:text-sm">
                            {mentor.name.split(' ')[0]}
                          </h3>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Duplicate row */}
                  <div
                    className="flex animate-scroll-right gap-2 pl-2 sm:gap-2 sm:pl-2 md:gap-6 md:pl-4"
                    aria-hidden="true"
                  >
                    {secondHalfMentors.map((mentor) => (
                      <div
                        key={`${mentor.id}-duplicate`}
                        onClick={() => openMentorModal(mentor)}
                        className="group relative aspect-square h-20 shrink-0 cursor-pointer overflow-hidden rounded-lg shadow-lg sm:h-32 sm:rounded-xl md:h-36 md:rounded-2xl lg:h-32 xl:h-44"
                      >
                        <img
                          src={mentor.image}
                          alt={mentor.name}
                          className="size-full object-cover transition-transform duration-300 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div
                          className="absolute inset-0 z-10 rounded-lg sm:rounded-xl md:rounded-2xl"
                          style={{
                            background:
                              'linear-gradient(to top, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 80%, rgba(0, 0, 0, 0) 100%)',
                          }}
                        />
                        <div className="absolute inset-x-2 bottom-1 z-10 sm:bottom-2 md:inset-x-4 md:bottom-4">
                          <h3 className="px-1 text-[10px] font-semibold text-white sm:text-xs md:text-sm">
                            {mentor.name.split(' ')[0]}
                          </h3>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ========== PARTNERS SECTION ========== */}
            <div className="py-6">
              <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
                <div className="text-center">
                  <span className="text-base font-light uppercase text-gray-500">Powered by</span>
                  <div className="mt-8 flex flex-wrap items-center justify-center gap-8 sm:gap-12">
                    <div className="hover:text-gray-800">
                      <img
                        className="h-9 pt-2 opacity-80 grayscale invert"
                        src="./assets/flandersgamehublogo.svg"
                        alt="Flanders Game Hub"
                      />
                    </div>
                    <div className="hover:text-gray-800">
                      <img
                        className="h-11 opacity-60 grayscale"
                        src="./assets/forsvclogo.webp"
                        alt="ForsVC"
                      />
                    </div>
                    <div className="pt-4 opacity-60 hover:text-gray-800">
                      <img className="h-12 grayscale" src="./assets/flegalogo.webp" alt="Flega" />
                    </div>
                    <div className="pt-4 opacity-60 hover:text-gray-800">
                      <img className="h-10 grayscale" src="./assets/aws.webp" alt="AWS" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* ========== END PARTNERS SECTION ========== */}
          </div>
        </div>
      </div>
      {/* ========== END HERO SECTION ========== */}

      {/* ========== EXPERT MENTORS SECTION (Flipped with 1/3 and 2/3 columns) ========== */}
      <section className="bg-white py-16 antialiased sm:py-24">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-6 text-xl font-medium tracking-tight text-gray-900 sm:mb-12 sm:text-3xl md:text-4xl">
            Expert Mentors for Every Challenge
          </h2>
          {/* 2 Column Grid with flipped order and proportional widths */}
          <div className="grid grid-cols-1 sm:gap-8 md:gap-12 lg:grid-cols-5 lg:gap-8">
            {/* Second Column: 2/3 width (list now first on larger screens) */}
            <div className="order-2 col-span-2 flex items-start pt-0 md:mt-4 lg:mt-1">
              <ul
                role="list"
                className="w-full space-y-3 border-gray-200 sm:space-y-4 md:space-y-5"
              >
                <li className="flex space-x-3">
                  {/* Icon */}
                  <svg
                    className="mt-0.5 size-5 shrink-0 text-red-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-base font-medium leading-tight text-gray-700">
                    Global Game Industry Mentors
                  </span>
                </li>
                <li className="flex space-x-3">
                  {/* Icon */}
                  <svg
                    className="size-5 shrink-0 text-red-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-base font-medium leading-tight text-gray-700">
                    Book Your Free 30-Minute Mentorship Call
                  </span>
                </li>
                <li className="flex space-x-3">
                  {/* Icon */}
                  <svg
                    className="size-5 shrink-0 text-red-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-base font-medium leading-tight text-gray-700">
                    Flexible Mentorship, Tailored to You
                  </span>
                </li>
                <li className="flex space-x-3">
                  {/* Icon */}
                  <svg
                    className="size-5 shrink-0 text-red-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-base font-medium leading-tight text-gray-700">
                    Build Your Mentor Team for Diverse Expertise
                  </span>
                </li>
              </ul>
            </div>
            {/* First Column: 1/3 width (now second on large screens) */}
            <div className="order-1 col-span-3">
              <p className="mb-4 max-w-[50ch] text-base font-normal text-gray-500 sm:text-xl">
                Our hand-picked mentors are industry veterans, specializing in everything your game
                studio needs – from leadership and legal to game design and business strategy.
              </p>
              <p className="mb-8 max-w-[50ch] text-base font-normal text-gray-500 sm:mb-0 sm:text-xl">
                Ready to overcome your hurdles? Browse mentors, find your perfect match, and book
                your free 30-minute consultation today.
                <a href="/mentors" className="ml-1 underline hover:text-red-700">
                  Find your mentor now.
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* ========== END EXPERT MENTORS SECTION ========== */}

      {/* ========== STARTUPS AND SCALE-UPS SECTION ========== */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <h3 className="mb-8 text-xl font-medium tracking-tight text-gray-900 sm:text-3xl md:text-4xl">
            Here Are Some Suggestions For Mentorship
          </h3>
          <div className="mx-auto grid grid-cols-1 gap-8 rounded-lg bg-white px-4 py-8 sm:gap-x-16 sm:gap-y-12 sm:px-8 sm:py-12 lg:grid-cols-2">
            {/* First Column */}
            <div className="space-y-8">
              {firstColumnCategories.map((category) => (
                <div key={category.id}>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch">
                    <p
                      className={`w-16 shrink-0 pt-1 text-sm font-medium ${getCategoryColor(category.category)} sm:text-right`}
                    >
                      {category.category}
                    </p>
                    <div className="hidden w-px bg-gray-200 sm:block sm:shrink-0" />
                    <div className="mb-4 flex-1 space-y-2">
                      <h4 className="text-xl font-bold text-gray-700">{category.title}</h4>
                      <p className="prose mx-auto max-w-none text-gray-500">
                        {category.description}
                      </p>
                      <div ref={suggestionsSection.ref} className="mt-4 flex flex-wrap gap-2">
                        {getMentorsForCategory(category.id).map((mentor) => {
                          return (
                            <button
                              key={mentor.id}
                              onClick={() => openMentorModal(mentor)}
                              className={`relative size-12 cursor-pointer overflow-hidden rounded-full border-2 transition-all duration-300 hover:scale-110`}
                              style={{
                                borderColor: `var(--${getBaseColor(mentor.expertise)}-300)`,
                              }}
                            >
                              <img
                                src={mentor.image}
                                alt={mentor.name}
                                className="size-full object-cover"
                                loading="lazy"
                              />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Second Column */}
            <div className="space-y-8">
              {secondColumnCategories.map((category) => (
                <div key={category.id}>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch">
                    <p
                      className={`w-16 shrink-0 pt-1 text-sm font-medium ${getCategoryColor(category.category)} sm:text-right`}
                    >
                      {category.category}
                    </p>
                    <div className="hidden w-px bg-gray-200 sm:block sm:shrink-0" />
                    <div className="mb-4 flex-1 space-y-2">
                      <h4 className="text-xl font-bold text-gray-700">{category.title}</h4>
                      <p className="prose mx-auto max-w-none text-gray-500">
                        {category.description}
                      </p>
                      <div ref={suggestionsSection.ref} className="mt-4 flex flex-wrap gap-2">
                        {getMentorsForCategory(category.id).map((mentor) => {
                          return (
                            <button
                              key={mentor.id}
                              onClick={() => openMentorModal(mentor)}
                              className={`relative size-12 cursor-pointer overflow-hidden rounded-full border-2 transition-all duration-300 hover:scale-110`}
                              style={{
                                borderColor: `var(--${getBaseColor(mentor.expertise)}-300)`,
                              }}
                            >
                              <img
                                src={mentor.image}
                                alt={mentor.name}
                                className="size-full object-cover"
                                loading="lazy"
                              />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Help finding mentor section */}
          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center">
              <div className="mt-8">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
                  <div className="flex items-center gap-6">
                    <button
                      onClick={() =>
                        openMentorModal(mentors.find((m) => m.id === 'eric-diepeveen')!)
                      }
                      className="size-12 cursor-pointer overflow-hidden rounded-full border-2 transition-all duration-300 hover:scale-110"
                      style={{
                        borderColor: `var(--${getBaseColor(['Business & Strategy'])}-300)`,
                      }}
                    >
                      <img
                        src="/assets/ericdiepeveen.webp"
                        alt="Eric Diepeveen"
                        className="size-full object-cover"
                      />
                    </button>
                    <div>
                      <h2 className="mb-1 text-base font-medium text-gray-900">
                        Need help finding the right mentor?
                      </h2>
                      <button
                        onClick={() =>
                          openMentorModal(mentors.find((m) => m.id === 'eric-diepeveen')!)
                        }
                        className="inline-flex items-center text-base font-light text-red-600 hover:text-red-700"
                      >
                        Send a message to Eric
                        <svg
                          className="ml-1 size-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <a
                    href="/mentors"
                    className="ml-8 inline-flex items-center justify-center rounded-full bg-red-600 px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red-700 sm:w-auto"
                  >
                    Browse all mentors
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FAQ SECTION ========== */}
      <section className="bg-white py-16 antialiased sm:py-24">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center sm:mb-16">
            <h2 className="text-xl font-medium tracking-tight text-gray-900 sm:text-3xl md:text-4xl">
              Any Questions?
            </h2>
          </div>
          <div className="mx-auto max-w-3xl divide-y divide-gray-200">
            {/* FAQ Items */}
            <div className="space-y-6">
              {/* FAQ Item 1 */}
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-xl font-medium text-gray-900 [&::-moz-list-bullet]:hidden [&::-webkit-details-marker]:hidden">
                  <span>Can I get a mentor?</span>
                  <span className="transition group-open:rotate-180">
                    <svg
                      fill="none"
                      height="24"
                      shapeRendering="geometricPrecision"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      viewBox="0 0 24 24"
                      width="24"
                    >
                      <path d="M6 9l6 6 6-6"></path>
                    </svg>
                  </span>
                </summary>
                <div className="pb-6">
                  <p className="text-base font-light leading-relaxed text-gray-700">
                    Yes, this platform is designed specifically for game industry professionals. You
                    don't need to be a studio owner to benefit from mentorship here — if you're
                    working in the game industry in any capacity, you're welcome to use this
                    platform to connect with experienced mentors. Whether you're part of an
                    established company or an independent developer, we're here to support your
                    career growth.
                  </p>
                </div>
              </details>

              {/* FAQ Item 2 */}
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-xl font-medium text-gray-900 [&::-moz-list-bullet]:hidden [&::-webkit-details-marker]:hidden">
                  <span>Can I have more than one mentor?</span>
                  <span className="transition group-open:rotate-180">
                    <svg
                      fill="none"
                      height="24"
                      shapeRendering="geometricPrecision"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      viewBox="0 0 24 24"
                      width="24"
                    >
                      <path d="M6 9l6 6 6-6"></path>
                    </svg>
                  </span>
                </summary>
                <div className="pb-6">
                  <p className="text-base font-light leading-relaxed text-gray-700">
                    Yes, you're welcome to contact multiple mentors! It's important to find the
                    right match for your needs, but please keep in mind that a mentor's time is
                    valuable. Building a strong relationship with one or more mentors is key to
                    making the most of the mentorship experience.
                  </p>
                </div>
              </details>

              {/* FAQ Item 3 */}
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-xl font-medium text-gray-900 [&::-moz-list-bullet]:hidden [&::-webkit-details-marker]:hidden">
                  <span>How much does a mentor cost?</span>
                  <span className="transition group-open:rotate-180">
                    <svg
                      fill="none"
                      height="24"
                      shapeRendering="geometricPrecision"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      viewBox="0 0 24 24"
                      width="24"
                    >
                      <path d="M6 9l6 6 6-6"></path>
                    </svg>
                  </span>
                </summary>
                <div className="pb-6">
                  <p className="text-base font-light leading-relaxed text-gray-700">
                    You can reach out to any mentor free of charge. If a mentor is interested in
                    working with you, they will arrange a complimentary 30-minute introductory
                    meeting to discuss your needs and assess fit. After the initial meeting, it's
                    entirely up to you and the mentor to decide how to proceed with any ongoing
                    mentorship.
                  </p>
                </div>
              </details>

              {/* FAQ Item 4 */}
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-xl font-medium text-gray-900 [&::-moz-list-bullet]:hidden [&::-webkit-details-marker]:hidden">
                  <span>Who are these mentors?</span>
                  <span className="transition group-open:rotate-180">
                    <svg
                      fill="none"
                      height="24"
                      shapeRendering="geometricPrecision"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      viewBox="0 0 24 24"
                      width="24"
                    >
                      <path d="M6 9l6 6 6-6"></path>
                    </svg>
                  </span>
                </summary>
                <div className="pb-6">
                  <p className="text-base font-light leading-relaxed text-gray-700">
                    Our mentors are seasoned industry veterans with years of hands-on experience in
                    the game industry from around the globe. Many are entrepreneurs who run their
                    own studios, while others specialize in specific areas such as game design,
                    programming, art, marketing, and business development. Regardless of their
                    specialty, all our mentors are dedicated to helping you grow and succeed through
                    their mentorship.
                  </p>
                </div>
              </details>

              {/* FAQ Item 5 */}
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-xl font-medium text-gray-900 [&::-moz-list-bullet]:hidden [&::-webkit-details-marker]:hidden">
                  <span>Can I meet a mentor in person?</span>
                  <span className="transition group-open:rotate-180">
                    <svg
                      fill="none"
                      height="24"
                      shapeRendering="geometricPrecision"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      viewBox="0 0 24 24"
                      width="24"
                    >
                      <path d="M6 9l6 6 6-6"></path>
                    </svg>
                  </span>
                </summary>
                <div className="pb-6">
                  <p className="text-base font-light leading-relaxed text-gray-700">
                    Yes, we encourage you to decide how you'd prefer to meet with your mentor. When
                    reaching out, it's helpful to let the mentor know your preferred meeting format
                    — whether it's an in-person meeting, a virtual chat, or a phone call. Our
                    mentors are flexible and will do their best to accommodate your needs.
                  </p>
                </div>
              </details>

              {/* FAQ Item 6 */}
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-xl font-medium text-gray-900 [&::-moz-list-bullet]:hidden [&::-webkit-details-marker]:hidden">
                  <span>Can you help me find the right match?</span>
                  <span className="transition group-open:rotate-180">
                    <svg
                      fill="none"
                      height="24"
                      shapeRendering="geometricPrecision"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      viewBox="0 0 24 24"
                      width="24"
                    >
                      <path d="M6 9l6 6 6-6"></path>
                    </svg>
                  </span>
                </summary>
                <div className="pb-6">
                  <p className="text-base font-light leading-relaxed text-gray-700">
                    Of course! If you're unsure who to reach out to, feel free to send us a message.
                    We'll do our best to connect you with the mentor who best aligns with your needs
                    and professional goals.
                  </p>
                  <button
                    onClick={() => openMentorModal(mentors.find((m) => m.id === 'eric-diepeveen')!)}
                    className="mt-4 inline-flex items-center text-red-600 hover:text-red-700"
                  >
                    Request a match
                    <svg
                      className="ml-1 size-6"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </details>
            </div>
          </div>
        </div>
      </section>
      {/* ========== END FAQ SECTION ========== */}
    </>
  );
};

export default LandingPage;
